import { useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmModal from "components/ConfirmModal";
import dispatchChartService from "api/dispatchChartService";
import { IRefreshReceiverDataItem } from "dto/Mailroom/IDispatchChart";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import { Props } from "./typings";

// Sygnatura i powód dla kilku pierwszych przesyłek z odpowiedzi.
const describe = (items: IRefreshReceiverDataItem[]) =>
  items
    .slice(0, 3)
    .map((x) => (x.ParcelNumber ? `${x.ParcelNumber}: ${x.Message ?? ""}` : x.Message ?? ""))
    .join(" ");

// „Aktualizuj dane adresata” — przesyłki, które jeszcze nie wyszły, dostają bieżące dane podmiotu
// z książki adresowej (jego aktualną wersję). Nadane i dołączone do karty nadawczej zostają przy
// dotychczasowych danych — BE zwraca je w Failed z powodem.
const RefreshReceiverDataModal = ({ parcelIds, onClose, onDone }: Props) => {
  const { t } = useTranslation();
  const { mutate } = dispatchChartService.useRefreshReceiverData();

  // Po akceptacji okno znika, ale komponent zostaje do końca zapisu — po odmontowaniu
  // react-query nie wywołałby callbacków przekazanych do mutate.
  const [running, setRunning] = useState(false);

  const accept = () => {
    setRunning(true);
    mutate(
      { ParcelIds: parcelIds },
      {
        onSuccess: ({ data }) => {
          if (data.Updated > 0) {
            notification({
              type: ENotificationType.SUCCESS,
              title: t("receiverData.updatedTitle"),
              text: t("receiverData.updatedText", { count: data.Updated }),
            });
          } else if (data.Failed.length === 0) {
            notification({
              type: ENotificationType.INFO,
              title: t("receiverData.unchangedTitle"),
              text: t("receiverData.unchangedText"),
            });
          }
          if (data.RegenerateLetters.length > 0) {
            notification({
              type: ENotificationType.INFO,
              title: t("receiverData.regenerateTitle"),
              text: describe(data.RegenerateLetters),
            });
          }
          if (data.Failed.length > 0) {
            notification({
              type: data.Updated > 0 ? ENotificationType.INFO : ENotificationType.ERROR,
              title: t("receiverData.skippedTitle", { count: data.Failed.length }),
              text: describe(data.Failed),
            });
          }
          if (data.Updated > 0) onDone?.();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: t("receiverData.errorTitle"),
            text: getErrorMessage(e) || t("receiverData.errorText"),
          }),
        onSettled: onClose,
      }
    );
  };

  if (running) return null;

  return (
    <ConfirmModal
      title={t("receiverData.refresh")}
      text={t("receiverData.confirmText", { count: parcelIds.length })}
      acceptLabel={t("receiverData.refreshAccept")}
      cancelLabel={t("common.cancel")}
      showDontShowNextTime={false}
      onAccept={accept}
      onCancel={onClose}
    />
  );
};

export default RefreshReceiverDataModal;
